// src/pages/Trend/components/analysis/product-analysis/FeatureAnalysisChart.js
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';
import './FeatureAnalysisChart.css';

/**
 * Feature Analysis Chart with Tabbed Interface
 * Matches the UI style of other metric charts
 */
const FeatureAnalysisChart = ({ data }) => {
  const { translate } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);

  // Extract values before any conditionals
  const hasSecondary = !!data?.secondary?.feature_analysis;
  const primary = data?.primary;
  const secondary = data?.secondary;

  // Get all feature categories (excluding color) - always call useMemo
  const featureTabs = useMemo(() => {
    if (!primary?.feature_analysis) return [];

    const productColorKey = 'trendPage.productAnalysis.features.productColor';
    return Object.entries(primary.feature_analysis)
      .filter(([key]) => key !== translate(productColorKey) && key !== '제품 컬러')
      .map(([title, primaryData]) => ({
        title,
        primaryData,
        secondaryData: hasSecondary ? secondary?.feature_analysis?.[title] : null
      }));
  }, [primary?.feature_analysis, secondary, hasSecondary, translate]);

  // Check for data after all hooks are called
  if (!data?.primary?.feature_analysis || featureTabs.length === 0) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noFeatureData')}
      </div>
    );
  }

  const currentTab = featureTabs[activeTab];

  // Calculate narrative for current tab
  const getNarrative = (tab) => {
    if (!tab || !tab.primaryData?.labels || !tab.primaryData?.values) return '';

    const { labels, values: primaryValues } = tab.primaryData;
    const primaryMax = Math.max(...primaryValues);
    const primaryMaxIndex = primaryValues.indexOf(primaryMax);
    const primaryMaxLabel = labels[primaryMaxIndex];
    const primaryTotal = primaryValues.reduce((sum, val) => sum + val, 0);
    const primaryMaxPercent = ((primaryMax / primaryTotal) * 100).toFixed(0);

    if (!hasSecondary) {
      return (
        <span>
          <span style={{
            fontWeight: '600',
            color: BBC_STYLE.HIGHLIGHT_COLOR,
            borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`,
            paddingBottom: '1px'
          }}>
            {primaryMaxLabel}
          </span>
          {' '}{translate('trendPage.productAnalysis.narratives.feature.mostSelected')}{' '}
          <span style={{
            fontWeight: '600',
            backgroundColor: 'rgba(250, 171, 24, 0.15)',
            padding: '0 4px',
            borderRadius: '2px'
          }}>
            {primaryMaxPercent}%
          </span>
          {' '}{translate('trendPage.productAnalysis.narratives.feature.share')}
        </span>
      );
    }

    // With secondary data
    const secondaryValues = tab.secondaryData?.values || [];
    const secondaryMax = Math.max(...secondaryValues);
    const secondaryMaxIndex = secondaryValues.indexOf(secondaryMax);
    const secondaryMaxLabel = labels[secondaryMaxIndex];
    const secondaryTotal = secondaryValues.reduce((sum, val) => sum + val, 0);
    const secondaryMaxPercent = ((secondaryMax / secondaryTotal) * 100).toFixed(0);

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <span>
          {primaryMaxLabel === secondaryMaxLabel ? (
            <>
              <span style={{
                fontWeight: '600',
                color: BBC_STYLE.HIGHLIGHT_COLOR
              }}>
                {primaryMaxLabel}
              </span>
              {' '}{translate('trendPage.productAnalysis.narratives.feature.ranksBothFilters')}
            </>
          ) : translate('trendPage.productAnalysis.narratives.feature.differentPreferences')}
        </span>
        <span style={{ margin: '0 4px', color: '#D0D0D0', fontWeight: '300' }}>｜</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR,
            display: 'inline-block'
          }} />
          <span style={{ fontWeight: '500', color: BBC_STYLE.PRIMARY_COLOR }}>
            {primaryMaxLabel}
          </span>
          <span style={{ fontSize: '0.85rem' }}>({primaryMaxPercent}%)</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR,
            display: 'inline-block'
          }} />
          <span style={{ fontWeight: '500', color: BBC_STYLE.SECONDARY_COLOR }}>
            {secondaryMaxLabel}
          </span>
          <span style={{ fontSize: '0.85rem' }}>({secondaryMaxPercent}%)</span>
        </span>
      </div>
    );
  };

  // Calculate styling for value badge - combines background color and font weight
  const getValueStyle = (value, maxValue, isSecondary = false) => {
    if (value === 0) {
      return {
        backgroundColor: 'transparent',
        fontWeight: '400'
      };
    }

    const intensity = (value / maxValue);
    const fontWeight = 400 + Math.round(intensity * 300); // 400 to 700

    if (!isSecondary) {
      return {
        backgroundColor: `rgba(19, 128, 161, ${0.12 + intensity * 0.35})`, // 0.12 to 0.47 opacity
        fontWeight: fontWeight.toString()
      };
    } else {
      return {
        backgroundColor: `rgba(250, 171, 24, ${0.12 + intensity * 0.35})`, // 0.12 to 0.47 opacity
        fontWeight: fontWeight.toString()
      };
    }
  };

  return (
    <ChartContainer autoHeight={true}>
      <div className="feature-analysis-chart">
        {/* Title */}
        <h3 style={{
          fontSize: '0.85rem',
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          margin: '0 0 12px 0'
        }}>
          특성분석
        </h3>

        {/* Tab Navigation */}
        <div className="feature-tabs">
          {featureTabs.map((tab, index) => (
            <button
              key={tab.title}
              className={`feature-tab ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {tab.title}
            </button>
          ))}
        </div>

        {/* Narrative below tabs */}
        <p style={{
          fontSize: '0.8rem',
          color: BBC_STYLE.LIGHT_TEXT,
          margin: '8px 0 12px 0',
          fontWeight: '400',
          lineHeight: '1.4'
        }}>
          {getNarrative(currentTab)}
        </p>

        {/* Tab Content */}
        <div className="feature-tab-content">
          <FeatureTable
            data={currentTab}
            hasSecondary={hasSecondary}
            translate={translate}
            getValueStyle={getValueStyle}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

/**
 * Feature Table Component
 */
const FeatureTable = ({ data, hasSecondary, translate, getValueStyle }) => {
  if (!data?.primaryData?.labels || !data?.primaryData?.values) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noAnalysisData')}
      </div>
    );
  }

  const { labels, values: primaryValues } = data.primaryData;
  const secondaryValues = data.secondaryData?.values || [];
  const maxValue = Math.max(...primaryValues, ...(hasSecondary ? secondaryValues : []));

  return (
    <div className="feature-table-container">
      <table className="feature-table">
        <colgroup>
          {hasSecondary && <col style={{ width: '10%' }} />}
          {labels.map((_, index) => (
            <col
              key={index}
              style={{ width: `${hasSecondary ? 90 / labels.length : 100 / labels.length}%` }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {hasSecondary && (
              <th className="feature-table-set-header">
                {translate('trendPage.productAnalysis.table.set')}
              </th>
            )}
            {labels.map((label) => (
              <th key={label} className="feature-table-label-header">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {hasSecondary && (
              <td className="feature-table-set-cell">
                {translate('trendPage.filters.set1')}
              </td>
            )}
            {primaryValues.map((value, index) => {
              const isMax = value === Math.max(...primaryValues);
              const valueStyle = getValueStyle(value, maxValue, false);
              return (
                <td
                  key={index}
                  className="feature-table-value-cell"
                >
                  <div className="value-container">
                    <span
                      className={`feature-value ${isMax ? 'is-max' : ''}`}
                      style={{
                        ...valueStyle,
                        padding: value > 0 ? '4px 10px' : '4px 6px',
                        borderRadius: '6px',
                        display: 'inline-block',
                        minWidth: '32px',
                        position: 'relative',
                        color: isMax ? '#1380A1' : '#1f2937'
                      }}
                    >
                      {value}
                      {isMax && value > 0 && (
                        <div className="max-indicator"
                          style={{ backgroundColor: BBC_STYLE.HIGHLIGHT_COLOR }}
                        />
                      )}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
          {hasSecondary && (
            <tr>
              <td className="feature-table-set-cell">
                {translate('trendPage.filters.set2')}
              </td>
              {labels.map((_, index) => {
                const value = secondaryValues[index] || 0;
                const isMax = value === Math.max(...secondaryValues);
                const valueStyle = getValueStyle(value, maxValue, true);
                return (
                  <td
                    key={index}
                    className="feature-table-value-cell"
                  >
                    <div className="value-container">
                      <span
                        className={`feature-value ${isMax ? 'is-max' : ''}`}
                        style={{
                          ...valueStyle,
                          padding: value > 0 ? '4px 10px' : '4px 6px',
                          borderRadius: '6px',
                          display: 'inline-block',
                          minWidth: '32px',
                          position: 'relative',
                          color: isMax ? '#FAAB18' : '#1f2937'
                        }}
                      >
                        {value}
                        {isMax && value > 0 && (
                          <div className="max-indicator"
                            style={{ backgroundColor: BBC_STYLE.SECONDARY_COLOR }}
                          />
                        )}
                      </span>
                    </div>
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// PropTypes
FeatureAnalysisChart.propTypes = {
  data: PropTypes.shape({
    primary: PropTypes.shape({
      feature_analysis: PropTypes.objectOf(
        PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string),
          values: PropTypes.arrayOf(PropTypes.number)
        })
      )
    }),
    secondary: PropTypes.shape({
      feature_analysis: PropTypes.objectOf(
        PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string),
          values: PropTypes.arrayOf(PropTypes.number)
        })
      )
    })
  })
};

FeatureTable.propTypes = {
  data: PropTypes.object.isRequired,
  hasSecondary: PropTypes.bool.isRequired,
  translate: PropTypes.func.isRequired,
  getValueStyle: PropTypes.func.isRequired
};

export default FeatureAnalysisChart;
